import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  license_type: string;
  license_status: string;
  license_expires_at: string | null;
  license_requested_at: string | null;
  license_requested_type: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isLicensed: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  requestLicense: (type: 'trial' | 'permanent') => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_URL = process.env.REACT_APP_API_URL || 'https://physio-scheduler-api.onrender.com/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role === 'admin';
  const isLicensed = isAdmin || (user?.license_type !== 'none' && user?.license_status === 'active');

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));

      // Verify token is still valid and get fresh user data
      fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      })
        .then(async (res) => {
          if (!res.ok) {
            // Token invalid, clear storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUser');
            setToken(null);
            setUser(null);
          } else {
            const data = await res.json();
            if (data.success && data.user) {
              setUser(data.user);
              localStorage.setItem('authUser', JSON.stringify(data.user));
            }
          }
        })
        .catch(() => {
          // Network error, keep local state but may need re-auth
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      const errorMessage = typeof data.error === 'string' ? data.error : data.error?.message || 'Login failed';
      throw new Error(errorMessage);
    }

    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
  };

  const register = async (email: string, password: string, name: string) => {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    const data = await res.json();

    if (!res.ok) {
      const errorMessage = typeof data.error === 'string' ? data.error : data.error?.message || 'Registration failed';
      throw new Error(errorMessage);
    }

    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  const requestLicense = async (type: 'trial' | 'permanent') => {
    const currentToken = localStorage.getItem('authToken');
    if (!currentToken) throw new Error('Not authenticated');

    const res = await fetch(`${API_URL}/auth/request-license`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ type }),
    });

    const data = await res.json();

    if (!res.ok) {
      const errorMessage = typeof data.error === 'string' ? data.error : data.error?.message || 'Request failed';
      throw new Error(errorMessage);
    }

    if (data.user) {
      setUser(data.user);
      localStorage.setItem('authUser', JSON.stringify(data.user));
    }
  };

  const refreshUser = async () => {
    const currentToken = localStorage.getItem('authToken');
    if (!currentToken) return;

    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          setUser(data.user);
          localStorage.setItem('authUser', JSON.stringify(data.user));
        }
      }
    } catch {
      // Network error — keep existing state
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, token, isLoading, isLicensed, isAdmin, login, register, logout, requestLicense, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
